// Statement.jsx - Complete version with Delete + Download PDF functionality
// (Email-only mode: Invoice / Excel / Consolidated Invoice checkboxes & actions
//  commented out for now. Re-enable by uncommenting the marked blocks.)
import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, ButtonGroup, Dropdown, Table, Form, Alert, Modal, Spinner, InputGroup } from "react-bootstrap";
import Footer from "./../../../footer";
import Header from './../../../header/header';
import Navvbar from './../../../header/Navvbar';
import { FaCaretUp, FaCaretDown, FaPaperPlane, FaCheckCircle, FaExclamationCircle, FaFileInvoice, FaDownload, FaEnvelope, FaSearch, FaTimes, FaTrash, FaTrashAlt } from 'react-icons/fa';
import { useCurrency } from '../../../../context/currencyContext';
import windowManager from "../../../../services/windowManager";
import FinanceSidebar from "../Financesidebar";
import { FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import './statements.css';

const API_BASE = 'https://hms.backend.hiragroupcloud.com/api/statements';

// Single place to read the lab id from localStorage
const getUserLabId = () => {
    const id =
        localStorage.getItem("userLabId") ||
        localStorage.getItem("userLabID") ||
        localStorage.getItem("userlabid");
    return id ? String(id).trim() : null;
};

const Statement = () => {
    const { formatAmount } = useCurrency();
    const [statements, setStatements] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteStatementDate, setDeleteStatementDate] = useState(null);
    const [includeConsolidatedInEmail, setIncludeConsolidatedInEmail] = useState(false);
    const [includeExcelInEmail, setIncludeExcelInEmail] = useState(false);
    const itemsPerPage = 10;
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'descending' });
    const navigate = useNavigate();

    // Month navigation state
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

    // Search state
    const [searchTerm, setSearchTerm] = useState('');

    // Delete states
    const [deleting, setDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // null or 'month'
    const [deleteAccountId, setDeleteAccountId] = useState(null);

    // Existing state for checkboxes and email sending
    const [selectedStatements, setSelectedStatements] = useState([]);
    // Page mode for each statement selected for email: 'single' or 'multiple'
    const [statementPageMode, setStatementPageMode] = useState({});

    const getStatementKey = (statement) => `${statement.accountId}-${statement.date || statement.runDate}`;
    const getPageMode = (statement) => statementPageMode[getStatementKey(statement)] || 'single';
    const setPageModeForStatement = (statement, mode) => {
        setStatementPageMode(prev => ({
            ...prev,
            [getStatementKey(statement)]: mode
        }));
    };
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [userStatementInfo, setUserStatementInfo] = useState(null);

    // ==================================================================
    // DOWNLOAD PDF — STATE
    // ==================================================================
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [downloadTarget, setDownloadTarget] = useState(null); // statement object being downloaded

    // ==================================================================
    // INVOICE / EXCEL / CONSOLIDATED — STATE (COMMENTED OUT FOR NOW)
    // ==================================================================
    const selectedForInvoice = [];
    const selectedForConsolidatedInvoice = [];
    const selectedForExcel = [];

    // Format functions
    const formatDateForURL = (d) => { if (!d) return 'N/A'; return new Date(d).toISOString().split('T')[0]; };
    const formatDateDisplay = (d) => { if (!d) return 'N/A'; return new Date(d).toISOString().split('T')[0]; };

    // Get current month name
    const getCurrentMonthName = () => {
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        return `${monthNames[currentMonth]} ${currentYear}`;
    };

    const handleEdit = (accountId, date) => {
        const formattedDate = formatDateForURL(date);
        navigate(`/edit-statement?accountIds=${accountId}&date=${formattedDate}`);
    };

    // ==================================================================
    // FETCH STATEMENTS — only for the logged-in lab (userLabId)
    // ==================================================================
    const fetchStatementsList = async () => {
        const userLabId = getUserLabId();
        if (!userLabId) {
            throw new Error('No userLabId found. Please log in again / select a lab.');
        }

        const response = await fetch(
            `${API_BASE}/statements?userLabId=${encodeURIComponent(userLabId)}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
            }
        );

        if (!response.ok) {
            throw new Error('Failed to fetch statements');
        }

        const { data } = await response.json();
        return data || [];
    };

    useEffect(() => {
        const fetchStatements = async () => {
            try {
                setLoading(true);
                const data = await fetchStatementsList();
                setStatements(data);
                await fetchUserStatementInfo();
            } catch (err) {
                setError(err.message);
                console.error("Fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchStatements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUserStatementInfo = async () => {
        try {
            const response = await fetch(`${API_BASE}/user-email`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
            });
            if (response.ok) {
                const data = await response.json();
                setUserStatementInfo(data);
            }
        } catch (err) {
            console.error('Error fetching user email info:', err);
        }
    };

    const handleDeleteSingleStatement = (accountId, date) => {
        setDeleteTarget('single');
        setDeleteAccountId(accountId);
        setDeleteStatementDate(date);
        setShowDeleteModal(true);
    };

    const handleDeleteMonthStatements = () => {
        setDeleteTarget('month');
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            setDeleting(true);
            setShowDeleteModal(false);

            const userLabId = getUserLabId();
            let response;

            if (deleteTarget === 'single') {
                response = await fetch(`${API_BASE}/delete-statement`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        accountId: deleteAccountId,
                        date: deleteStatementDate,
                        userLabId
                    })
                });
            } else if (deleteTarget === 'month') {
                response = await fetch(`${API_BASE}/delete-statements-by-month`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        month: currentMonth,
                        year: currentYear,
                        userLabId
                    })
                });
            }

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to delete statement(s)');
            }

            setSendResult({
                message: result.message || 'Statement(s) deleted successfully',
                sent: [],
                failed: [],
                totalSelected: result.deletedCount || result.data?.deletedCount || 1,
                totalSent: result.deletedCount || result.data?.deletedCount || 1,
                totalFailed: 0
            });
            setShowResultModal(true);

            // Refresh list (lab-filtered)
            try {
                const data = await fetchStatementsList();
                setStatements(data);

                if (deleteTarget === 'single' && deleteAccountId && deleteStatementDate) {
                    setSelectedStatements(prev =>
                        prev.filter(s => !(s.accountId === deleteAccountId && s.date === deleteStatementDate))
                    );
                } else if (deleteTarget === 'month') {
                    setSelectedStatements([]);
                }
            } catch (refreshErr) {
                console.error('Error refreshing statements:', refreshErr);
            }

        } catch (err) {
            console.error('Error deleting statement(s):', err);
            setSendResult({
                message: 'Error deleting statement(s)',
                error: err.message,
                sent: [],
                failed: [{ account: 'Delete Error', error: err.message || 'Unknown error occurred' }],
                totalSelected: 0,
                totalSent: 0,
                totalFailed: 1
            });
            setShowResultModal(true);
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
            setDeleteAccountId(null);
            setDeleteStatementDate(null);
        }
    };

    // ── Email checkbox handlers ──
    const handleCheckboxChange = (statement) => {
        setSelectedStatements(prev => {
            const isSelected = prev.some(s =>
                (s.date === statement.date || s.date === statement.runDate) &&
                s.accountId === statement.accountId
            );
            if (isSelected) {
                return prev.filter(s =>
                    !((s.date === statement.date || s.date === statement.runDate) &&
                      s.accountId === statement.accountId)
                );
            } else {
                return [...prev, {
                    date: statement.date || statement.runDate,
                    accountId: statement.accountId,
                    accountName: statement.account || statement.accountHolder
                }];
            }
        });
    };

    const handleSelectAll = () => {
        const allCurrentPageKeys = currentStatements.map(s =>
            `${s.date || s.runDate}-${s.accountId}`
        );
        const allSelected = allCurrentPageKeys.every(key =>
            selectedStatements.some(s => `${s.date}-${s.accountId}` === key)
        );
        if (allSelected) {
            setSelectedStatements(prev =>
                prev.filter(selected =>
                    !currentStatements.some(current =>
                        (current.date === selected.date || current.runDate === selected.date) &&
                        current.accountId === selected.accountId
                    )
                )
            );
        } else {
            const newSelections = currentStatements
                .filter(current =>
                    !selectedStatements.some(selected =>
                        (selected.date === current.date || selected.date === current.runDate) &&
                        selected.accountId === current.accountId
                    )
                )
                .map(statement => ({
                    date: statement.date || statement.runDate,
                    accountId: statement.accountId,
                    accountName: statement.account || statement.accountHolder
                }));
            setSelectedStatements(prev => [...prev, ...newSelections]);
        }
    };

    const handleSendClick = () => {
        setIncludeConsolidatedInEmail(false);
        setIncludeExcelInEmail(false);
        setShowConfirmModal(true);
    };

    // ==================================================================
    // DOWNLOAD PDF — HANDLER (robust)
    // ==================================================================
    const triggerBrowserDownload = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    };

    const base64ToBlob = (base64, mime = 'application/pdf') => {
        const clean = base64.replace(/^data:.*;base64,/, '');
        const byteChars = atob(clean);
        const bytes = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
        return new Blob([bytes], { type: mime });
    };

    const handleDownloadPdf = async (statement) => {
        try {
            setDownloadingPdf(true);
            setDownloadTarget(statement);

            const userLabId = getUserLabId();

            if (!userLabId) {
                alert('No userLabId found. Please log in again / select a lab.');
                return;
            }

            const pageMode = getPageMode(statement);

            const payload = {
                userLabId,
                selectedStatements: [{
                    date: statement.date || statement.runDate,
                    accountId: statement.accountId,
                    pageMode
                }]
            };

            const response = await fetch(`${API_BASE}/download-statement`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                let errMsg = 'Failed to download statement';
                try {
                    const errData = await response.json();
                    errMsg = errData.message || errMsg;
                } catch (e) { /* ignore */ }
                throw new Error(errMsg);
            }

            let filename = `Statement_${statement.account || statement.accountId}_${formatDateForURL(statement.date)}.pdf`;
            const contentDisposition = response.headers.get('Content-Disposition');
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
                if (filenameMatch && filenameMatch[1]) {
                    filename = decodeURIComponent(filenameMatch[1].replace(/"/g, ''));
                }
            }
            if (!filename.toLowerCase().endsWith('.pdf')) filename += '.pdf';

            const contentType = (response.headers.get('Content-Type') || '').toLowerCase();
            let pdfBlob;

            if (contentType.includes('application/json')) {
                const json = await response.json();
                const base64 =
                    json.pdfBase64 || json.base64 || json.pdf || json.file ||
                    (typeof json.data === 'string' ? json.data : null) ||
                    json.data?.pdfBase64 || json.data?.base64 || json.data?.pdf;
                const fileUrl = json.url || json.pdfUrl || json.data?.url;

                if (base64) {
                    pdfBlob = base64ToBlob(base64);
                } else if (fileUrl) {
                    window.open(fileUrl, '_blank');
                    return;
                } else {
                    throw new Error(json.message || 'Server did not return a PDF file');
                }
            } else {
                const raw = await response.blob();
                pdfBlob = new Blob([raw], { type: 'application/pdf' });
            }

            if (!pdfBlob || pdfBlob.size === 0) {
                throw new Error('Received an empty PDF file');
            }

            triggerBrowserDownload(pdfBlob, filename);

        } catch (err) {
            console.error('Error downloading statement:', err);
            alert(`Error downloading statement: ${err.message}`);
        } finally {
            setDownloadingPdf(false);
            setDownloadTarget(null);
        }
    };

    // ── handleSendStatements ── (EMAIL ONLY)
    const handleSendStatements = async () => {
        try {
            setSending(true);
            setSendResult(null);

            const userLabId = getUserLabId();

            if (!userLabId) {
                setSendResult({
                    message: 'No userLabId found. Please log in again / select a lab.',
                    error: 'Missing userLabId in localStorage',
                    sent: [],
                    failed: [{ account: 'Session Error', error: 'userLabId missing in localStorage' }],
                    totalSelected: 0,
                    totalSent: 0,
                    totalFailed: 1
                });
                setShowResultModal(true);
                setSending(false);
                setShowConfirmModal(false);
                return;
            }

            if (selectedStatements.length === 0) {
                throw new Error('No statements selected for sending');
            }

            const payload = {
                userLabId,
                selectedStatements: selectedStatements.map(s => ({
                    date: s.date,
                    accountId: s.accountId,
                    pageMode: statementPageMode[`${s.accountId}-${s.date}`] || 'single'
                })),
                includeConsolidatedInEmail: false,
                includeExcelInEmail: false,
                consolidatedInvoice: null,
                excelAttachment: null
            };

            const response = await fetch(`${API_BASE}/send-statements`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Failed to send statements');

            setSendResult(result);
            setShowResultModal(true);
            if (result.totalFailed === 0) {
                setSelectedStatements([]);
                setIncludeConsolidatedInEmail(false);
                setIncludeExcelInEmail(false);
                setStatementPageMode({});
            }
        } catch (err) {
            console.error('Error sending statements:', err);
            setSendResult({
                message: 'Error sending statements',
                error: err.message,
                sent: [],
                failed: [{ account: 'Error', error: err.message || 'Unknown error occurred' }],
                totalSelected: selectedStatements.length,
                totalSent: 0,
                totalFailed: 1
            });
            setShowResultModal(true);
        } finally {
            setSending(false);
            setShowConfirmModal(false);
        }
    };

    const handleConfirmKeyPress = (e) => {
        if (e.key === 'Enter' && !sending) {
            e.preventDefault();
            handleSendStatements();
        }
    };
    const formatCurrencyValue = (value) => {
        let numericValue = Number(
            String(value || 0)
                .replace(/,/g, "")
                .replace(/[^\d.-]/g, "")
        );

        if (isNaN(numericValue)) {
            numericValue = 0;
        }

        return numericValue.toFixed(2);
    };

    const filteredByMonthStatements = useMemo(() => {
        return statements.filter(statement => {
            if (!statement.date) return false;
            const statementDate = new Date(statement.date);
            return (
                statementDate.getMonth() === currentMonth &&
                statementDate.getFullYear() === currentYear
            );
        });
    }, [statements, currentMonth, currentYear]);

    const searchFilteredStatements = useMemo(() => {
        if (!searchTerm.trim()) {
            return filteredByMonthStatements;
        }

        const searchLower = searchTerm.toLowerCase().trim();

        return filteredByMonthStatements.filter(statement => {
            const accountName = (statement.account || statement.accountHolder || '').toLowerCase();
            if (accountName.includes(searchLower)) return true;

            const accountId = String(statement.accountId || '').toLowerCase();
            if (accountId.includes(searchLower)) return true;

            const dateStr = statement.date ? formatDateDisplay(statement.date) : '';
            if (dateStr.includes(searchLower)) return true;

            const balanceFields = [
                statement.oneTo30Days,
                statement.thirtyOneTo60Days,
                statement.sixtyOneTo90Days,
                statement.ninetyPlusDays,
                statement.newBalance
            ];

            return balanceFields.some(field => {
                const fieldStr = String(field || '').toLowerCase();
                return fieldStr.includes(searchLower);
            });
        });
    }, [filteredByMonthStatements, searchTerm]);

    const processedStatements = useMemo(() => {
        return searchFilteredStatements.map(statement => ({
            ...statement,
            oneTo30Days:        formatCurrencyValue(statement.oneTo30Days),
            thirtyOneTo60Days:  formatCurrencyValue(statement.thirtyOneTo60Days),
            sixtyOneTo90Days:   formatCurrencyValue(statement.sixtyOneTo90Days),
            ninetyPlusDays:     formatCurrencyValue(statement.ninetyPlusDays),
            newBalance:         formatCurrencyValue(statement.newBalance)
        }));
    }, [searchFilteredStatements, formatAmount]);

    const sortedStatements = useMemo(() => {
        let sortableItems = [...processedStatements];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key.includes('Days') || sortConfig.key === 'newBalance') {
                    aValue = parseFloat(aValue.replace(/[^\d.-]/g, '')) || 0;
                    bValue = parseFloat(bValue.replace(/[^\d.-]/g, '')) || 0;
                } else if (sortConfig.key === 'date') {
                    aValue = new Date(aValue).getTime();
                    bValue = new Date(bValue).getTime();
                } else if (sortConfig.key === 'account') {
                    aValue = (aValue || '').toString().toLowerCase();
                    bValue = (bValue || '').toString().toLowerCase();
                }

                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ?  1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [processedStatements, sortConfig]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, currentMonth, currentYear]);

    const totalItems   = sortedStatements.length;
    const totalPages   = Math.ceil(totalItems / itemsPerPage);
    const startIndex   = (currentPage - 1) * itemsPerPage;
    const endIndex     = startIndex + itemsPerPage;
    const currentStatements = sortedStatements.slice(startIndex, endIndex);
    const showingFrom  = startIndex + 1;
    const showingTo    = Math.min(endIndex, totalItems);

    const handlePrevious = () => { if (currentPage > 1)          setCurrentPage(p => p - 1); };
    const handleNext     = () => { if (currentPage < totalPages)  setCurrentPage(p => p + 1); };

    const handlePreviousMonth = () => {
        setCurrentPage(1);
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const handleNextMonth = () => {
        setCurrentPage(1);
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const openInNewWindow = (url) => windowManager.openStatement(url);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
        setSortConfig({ key, direction });
    };

    const getSortIcons = (key) => (
        <span style={{ marginLeft: '5px', display: 'inline-flex', flexDirection: 'column', verticalAlign: 'middle' }}>
            <FaCaretUp  style={{ color: sortConfig.key === key && sortConfig.direction === 'ascending'  ? '#004aad' : 'black', fontSize: '8px', lineHeight: '4px' }} />
            <FaCaretDown style={{ color: sortConfig.key === key && sortConfig.direction === 'descending' ? '#004aad' : 'black', fontSize: '8px', lineHeight: '4px' }} />
        </span>
    );

    const isStatementSelected = (statement) =>
        selectedStatements.some(s =>
            (s.date === statement.date || s.date === statement.runDate) &&
            s.accountId === statement.accountId
        );

    const hasInvoiceOverlap = false;
    const hasConsolidatedOverlap = false;
    const hasExcelOverlap = false;

    const isOnlyConsolidatedInvoiceSelection = false;
    const isOnlyNonEmailSelection = false;
    const isOnlyExcelSelection = false;

    return (
        <>
            <Header />
            <Navvbar />
            <br />
            <div className="statement-wrapper" style={{ width: "95%", margin: "0 auto", padding: "0px" }}>
                <div className="row statement-main-row">
                    {/* Sidebar (shared component) */}
                    <FinanceSidebar variant="statement" />

                    {/* Main content */}
                    <div className="col-md-10 statement-main-col">
                        {/* Month display header */}
                        <div className="statement-month-header" style={{
                            backgroundColor: "#004aad",
                            color: "white",
                            padding: "10px 20px",
                            marginBottom: "15px",
                            borderRadius: "4px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}>
                            <h4 className="statement-month-header-title" style={{ margin: 0, fontWeight: "600" }}>
                                Statements for {getCurrentMonthName()}
                            </h4>
                            <div className="statement-month-header-info" style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                <span className="statement-month-header-count" style={{ fontSize: "14px", opacity: "0.9" }}>
                                    {totalItems} statement(s) found
                                </span>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    className="statement-delete-month-btn"
                                    onClick={handleDeleteMonthStatements}
                                    disabled={totalItems === 0 || deleting}
                                    style={{
                                        backgroundColor: "#dc3545",
                                        borderColor: "#dc3545",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        fontSize: "13px"
                                    }}
                                    title={`Delete all statements for ${getCurrentMonthName()}`}
                                >
                                    {deleting && deleteTarget === 'month' ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <FaTrashAlt />
                                    )}
                                    Delete All This Month
                                </Button>
                            </div>
                        </div>

                        {/* Search Bar */}
                        <div className="statement-search-bar" style={{
                            marginBottom: "15px",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}>
                            <InputGroup className="statement-search-input-group" style={{ maxWidth: "500px" }}>
                                <InputGroup.Text className="statement-search-icon-box" style={{
                                    backgroundColor: "#004aad",
                                    color: "white",
                                    border: "1px solid #004aad"
                                }}>
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by account name, ID, date, or amount..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="statement-search-input"
                                    style={{
                                        borderColor: "#004aad",
                                        boxShadow: "none"
                                    }}
                                />
                                {searchTerm && (
                                    <Button
                                        variant="outline-secondary"
                                        className="statement-search-clear-btn"
                                        onClick={handleClearSearch}
                                        style={{ borderColor: "#004aad" }}
                                    >
                                        <FaTimes />
                                    </Button>
                                )}
                            </InputGroup>
                            {searchTerm && (
                                <span className="statement-search-results" style={{
                                    color: "#004aad",
                                    fontSize: "14px",
                                    fontWeight: "500"
                                }}>
                                    Search results: {totalItems} item(s)
                                </span>
                            )}
                        </div>

                        {/* ── Email send banner ── */}
                        {selectedStatements.length > 0 && (
                            <div className="statement-email-banner" style={{
                                backgroundColor: "#e8f4fc",
                                padding: "10px 15px",
                                marginBottom: "10px",
                                borderLeft: "4px solid #004aad",
                                borderRadius: "4px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}>
                                <div>
                                    <span className="statement-email-banner-title" style={{ fontWeight: "500", color: "#004aad" }}>
                                        <FaCheckCircle style={{ marginRight: "8px" }} />
                                        {selectedStatements.length} statement(s) selected for email
                                    </span>
                                    <div className="statement-email-banner-accounts" style={{ fontSize: "14px", color: "#666", marginTop: "5px" }}>
                                        {selectedStatements.slice(0, 3).map((s, i) => (
                                            <span key={i}>
                                                {s.accountName} ({formatDateDisplay(s.date)}){i < selectedStatements.length - 1 ? ', ' : ''}
                                            </span>
                                        ))}
                                        {selectedStatements.length > 3 && ` and ${selectedStatements.length - 3} more...`}
                                    </div>
                                </div>
                                <Button
                                    variant="success"
                                    className="statement-send-btn"
                                    onClick={handleSendClick}
                                    disabled={sending}
                                    style={{ backgroundColor: "#28a745", borderColor: "#28a745", display: "flex", alignItems: "center", gap: "8px" }}
                                >
                                    {sending ? (
                                        <><Spinner animation="border" size="sm" /> Sending...</>
                                    ) : (
                                        <><FaPaperPlane /> Send {selectedStatements.length} Statement(s)</>
                                    )}
                                </Button>
                            </div>
                        )}

                        <div id="postings_wrapper" className="dataTables_wrapper statement-table-wrapper">
                            {/* Top bar */}
                            <div className="d-flex justify-content-between align-items-start statement-table-topbar" style={{ backgroundColor: "#e6e6e6", padding: "10px 15px", borderTop: "4px solid #004aad" }}>
                                <p className="statement-table-topbar-text" style={{ margin: 0, color: "#333", fontWeight: "500" }}>
                                    Showing {showingFrom} to {showingTo} of {totalItems} entries
                                </p>
                                <div className="statement-table-topbar-right" style={{ textAlign: "right" }}>

                                    <div className="statement-table-actions" style={{ display: "flex", gap: "6px", justifyContent: "flex-end", marginBottom: "5px" }}>
                                        <button
                                            className="statement-month-nav-btn"
                                            style={{
                                                backgroundColor: "#004aad",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "4px",
                                                padding: "5px 12px",
                                                fontSize: "14px",
                                                cursor: "pointer"
                                            }}
                                            onClick={handlePreviousMonth}
                                        >
                                            &lt; Previous Month
                                        </button>
                                        <button
                                            className="statement-month-nav-btn"
                                            style={{
                                                backgroundColor: "#004aad",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "4px",
                                                padding: "5px 12px",
                                                fontSize: "14px",
                                                cursor: "pointer"
                                            }}
                                            onClick={handleNextMonth}
                                        >
                                            Next Month &gt;
                                        </button>
                                    </div>

                                    <ButtonGroup>
                                        <Dropdown>
                                            <Dropdown.Toggle className="statement-actions-dropdown-toggle" style={{ backgroundColor: "#004aad", color: "#fff", border: "none", borderRadius: "4px", padding: "10px 14px", fontWeight: "600", fontSize: "14px" }}>
                                                Actions
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className="statement-actions-dropdown-menu" style={{ borderRadius: "6px", boxShadow: "0 5px 12px rgba(0,0,0,0.15)", padding: "8px 0", minWidth: "180px" }}>
                                                <Dropdown.Item onClick={() => openInNewWindow("/runStatements")}>Run Statements</Dropdown.Item>
                                                <Dropdown.Item onClick={() => openInNewWindow("/reprint-statements")}>Reprint Statements</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </ButtonGroup>
                                    <div className="statement-pagination" style={{ marginTop: "5px", display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                                        <button className="statement-pagination-btn" style={{ backgroundColor: "#001f3f", border: "none", color: "white", width: "20px", height: "20px", borderRadius: "50%", fontSize: "12px", lineHeight: "20px", padding: 0, cursor: currentPage === 1 ? "not-allowed" : "pointer", opacity: currentPage === 1 ? 0.5 : 1 }} onClick={handlePrevious} disabled={currentPage === 1}>←</button>
                                        <button className="statement-pagination-btn" style={{ backgroundColor: "#001f3f", border: "none", color: "white", width: "20px", height: "20px", borderRadius: "50%", fontSize: "12px", lineHeight: "20px", padding: 0, cursor: currentPage === totalPages ? "not-allowed" : "pointer", opacity: currentPage === totalPages ? 0.5 : 1 }} onClick={handleNext} disabled={currentPage === totalPages}>→</button>
                                    </div>
                                </div>
                            </div>

                            {loading ? (
                                <div className="statement-loading" style={{ textAlign: "center", padding: "40px" }}>
                                    <Spinner animation="border" variant="primary" />
                                    <p>Loading statements...</p>
                                </div>
                            ) : error ? (
                                <Alert variant="danger" style={{ margin: "20px" }}>
                                    <FaExclamationCircle style={{ marginRight: "8px" }} /> Error: {error}
                                </Alert>
                            ) : (
                                <Table hover responsive className="statement-table" style={{ borderCollapse: "collapse", padding: "10px 15px" }}>
                                    <thead className="statement-table-head" style={{ backgroundColor: "#e6e6e6", fontWeight: "500", color: "black" }}>
                                        <tr>
                                            <th className="statement-table-checkbox-cell" style={{ backgroundColor: "#e6e6e6", width: "80px" }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    title="Select all for email"
                                                    checked={currentStatements.length > 0 && currentStatements.every(s => isStatementSelected(s))}
                                                    onChange={handleSelectAll}
                                                    style={{ margin: 0 }}
                                                />
                                            </th>
                                            <th style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('date')}>
                                                Date {getSortIcons('date')}
                                            </th>
                                            <th style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('account')}>
                                                Account {getSortIcons('account')}
                                            </th>
                                            <th className="text-right" style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('oneTo30Days')}>
                                                1-30 Days {getSortIcons('oneTo30Days')}
                                            </th>
                                            <th className="text-right" style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('thirtyOneTo60Days')}>
                                                31-60 Days {getSortIcons('thirtyOneTo60Days')}
                                            </th>
                                            <th className="text-right" style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('sixtyOneTo90Days')}>
                                                61-90 Days {getSortIcons('sixtyOneTo90Days')}
                                            </th>
                                            <th className="text-right" style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('ninetyPlusDays')}>
                                                90+ Days {getSortIcons('ninetyPlusDays')}
                                            </th>
                                            <th className="text-right" style={{ backgroundColor: "#e6e6e6", cursor: 'pointer' }} onClick={() => requestSort('newBalance')}>
                                                New Balance {getSortIcons('newBalance')}
                                            </th>
                                            <th style={{ backgroundColor: "#e6e6e6", width: "220px" }}>Actions</th>
                                        </tr>
                                        <tr className="statement-table-subhead">
                                            <th
                                                colSpan={1}
                                                style={{
                                                    backgroundColor: "#f5f5f5",
                                                    padding: "4px 8px",
                                                    fontSize: "11px",
                                                    fontWeight: "400",
                                                    color: "#555"
                                                }}
                                            >
                                                <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        <Form.Check type="checkbox" readOnly checked style={{ pointerEvents: "none", margin: 0 }} />
                                                        <span style={{ marginLeft: "5px" }}>Email</span>
                                                    </div>
                                                </div>
                                            </th>
                                            <th colSpan={7} style={{ backgroundColor: "#f5f5f5" }}></th>
                                            <th style={{ backgroundColor: "#f5f5f5" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentStatements.length > 0 ? (
                                            currentStatements.map((statement) => {
                                                const isEmailSel   = isStatementSelected(statement);
                                                const isDownloading = downloadingPdf &&
                                                    downloadTarget &&
                                                    downloadTarget.accountId === statement.accountId &&
                                                    (downloadTarget.date === statement.date || downloadTarget.runDate === statement.date);

                                                let rowBgColor = undefined;
                                                if (isEmailSel) {
                                                    rowBgColor = "#e8f4fc";
                                                }

                                                return (
                                                    <tr key={`${statement.date || statement.runDate}-${statement.accountId}`}
                                                        style={{ backgroundColor: rowBgColor }}>
                                                        <td className="statement-table-checkbox-cell">
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={isEmailSel}
                                                                onChange={() => handleCheckboxChange(statement)}
                                                                style={{ margin: 0 }}
                                                                title="Select for email"
                                                            />
                                                            {isEmailSel && (
                                                                <div className="statement-page-mode-wrapper" style={{ display: 'flex', flexDirection: 'column', marginTop: '4px', gap: '2px' }}>
                                                                    <label className="statement-page-mode-label" style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name={`pageMode-${getStatementKey(statement)}`}
                                                                            checked={getPageMode(statement) === 'single'}
                                                                            onChange={() => setPageModeForStatement(statement, 'single')}
                                                                            style={{ margin: 0 }}
                                                                        />
                                                                        1 Page
                                                                    </label>
                                                                    <label className="statement-page-mode-label" style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                                        <input
                                                                            type="radio"
                                                                            name={`pageMode-${getStatementKey(statement)}`}
                                                                            checked={getPageMode(statement) === 'multiple'}
                                                                            onChange={() => setPageModeForStatement(statement, 'multiple')}
                                                                            style={{ margin: 0 }}
                                                                        />
                                                                        Several Pages
                                                                    </label>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="statement-date-cell" style={{ textAlign: 'center' }}>
                                                            <Link to={`/statements/by-date/${formatDateForURL(statement.date)}?accountIds=${statement.accountId}`} className="statement-date-link" style={{ color: "#004aad", textDecoration: "none" }}>
                                                                {formatDateDisplay(statement.date)}
                                                            </Link>
                                                        </td>
                                                        <td className="statement-account-cell">{statement.account}</td>
                                                        <td className="text-right statement-amount-cell">{statement.oneTo30Days}</td>
                                                        <td className="text-right statement-amount-cell">{statement.thirtyOneTo60Days}</td>
                                                        <td className="text-right statement-amount-cell">{statement.sixtyOneTo90Days}</td>
                                                        <td className="text-right statement-amount-cell">{statement.ninetyPlusDays}</td>
                                                        <td className="text-right statement-amount-cell">{statement.newBalance}</td>
                                                        <td className="statement-actions-cell">
                                                            <div className="statement-action-buttons" style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    className="statement-reprint-btn"
                                                                    style={{ backgroundColor: "#004aad", color: "white", borderColor: "#004aad", display: "flex", alignItems: "center", gap: "4px" }}
                                                                    onClick={() => openInNewWindow(`/run-date-range-statement?accountIds=${statement.accountId}&date=${formatDateForURL(statement.date)}`)}
                                                                >
                                                                    Reprint
                                                                </Button>
                                                                <Button
                                                                    variant="outline-success"
                                                                    size="sm"
                                                                    className="statement-download-btn"
                                                                    style={{
                                                                        color: "#28a745",
                                                                        borderColor: "#28a745",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "4px"
                                                                    }}
                                                                    onClick={() => handleDownloadPdf(statement)}
                                                                    disabled={isDownloading}
                                                                    title="Download PDF (same as emailed statement)"
                                                                >
                                                                    {isDownloading ? (
                                                                        <><Spinner animation="border" size="sm" /> <span>...</span></>
                                                                    ) : (
                                                                        <><FaDownload /> <span>PDF</span></>
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="statement-empty-cell" style={{ textAlign: "center", padding: "40px" }}>
                                                    {searchTerm ? (
                                                        <>No statements match your search "{searchTerm}" for {getCurrentMonthName()}</>
                                                    ) : (
                                                        <>No statements found for {getCurrentMonthName()}</>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}

                            {/* Bottom pagination bar */}
                            <div className="d-flex justify-content-between align-items-start statement-table-bottombar" style={{ backgroundColor: "#e6e6e6", padding: "10px 15px", borderTop: "4px solid #004aad" }}>
                                <p className="statement-table-topbar-text" style={{ margin: 0, color: "#333", fontWeight: "500" }}>
                                    Showing {showingFrom} to {showingTo} of {totalItems} entries
                                </p>
                                <div style={{ textAlign: "right" }}>
                                    <div className="statement-pagination" style={{ marginTop: "5px", display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                                        <button className="statement-pagination-btn" style={{ backgroundColor: "#001f3f", border: "none", color: "white", width: "20px", height: "20px", borderRadius: "50%", fontSize: "12px", lineHeight: "20px", padding: 0, cursor: currentPage === 1 ? "not-allowed" : "pointer", opacity: currentPage === 1 ? 0.5 : 1 }} onClick={handlePrevious} disabled={currentPage === 1}>←</button>
                                        <button className="statement-pagination-btn" style={{ backgroundColor: "#001f3f", border: "none", color: "white", width: "20px", height: "20px", borderRadius: "50%", fontSize: "12px", lineHeight: "20px", padding: 0, cursor: currentPage === totalPages ? "not-allowed" : "pointer", opacity: currentPage === totalPages ? 0.5 : 1 }} onClick={handleNext} disabled={currentPage === totalPages}>→</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Email Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered className="statement-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaPaperPlane style={{ marginRight: "10px", color: "#28a745" }} />
                        Confirm Send Statements
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to send {selectedStatements.length} statement(s) to the respective account emails?</p>

                    <div className="statement-selected-list" style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '15px', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                        <h6 className="statement-selected-list-title" style={{ color: '#004aad', marginBottom: '10px' }}>Selected Statements:</h6>
                        <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                            {selectedStatements.map((statement, index) => {
                                return (
                                    <li key={index} style={{ padding: '5px 0', borderBottom: index < selectedStatements.length - 1 ? '1px dashed #eee' : 'none' }}>
                                        <strong>{statement.accountName}</strong> - {formatDateDisplay(statement.date)}
                                        <br /><small style={{ color: '#666' }}>Account ID: {statement.accountId}</small>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={sending}>Cancel</Button>
                    <Button variant="success" onClick={handleSendStatements} onKeyPress={handleConfirmKeyPress} disabled={sending}>
                        {sending ? (<><Spinner animation="border" size="sm" style={{ marginRight: "8px" }} /> Sending...</>) : 'Send Statements'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="statement-modal">
                <Modal.Header closeButton className="statement-delete-modal-header" style={{ backgroundColor: "#dc3545", color: "white" }}>
                    <Modal.Title>
                        <FaTrashAlt style={{ marginRight: "10px" }} />
                        Confirm Delete
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteTarget === 'single' ? (
                        <div>
                            <div className="statement-warning-box" style={{
                                backgroundColor: "#fff3cd",
                                padding: "12px",
                                borderRadius: "4px",
                                borderLeft: "4px solid #ffc107",
                                marginBottom: "15px"
                            }}>
                                <strong style={{ color: "#856404" }}>
                                    <FaExclamationCircle style={{ marginRight: "8px" }} />
                                    Warning: This action cannot be undone!
                                </strong>
                            </div>
                            <p>Are you sure you want to delete the statement for:</p>
                            <div className="statement-delete-info-box" style={{
                                backgroundColor: "#f8f9fa",
                                padding: "10px",
                                borderRadius: "4px",
                                border: "1px solid #dee2e6"
                            }}>
                                <strong>Account ID:</strong> {deleteAccountId}<br />
                                <strong>Month:</strong> {getCurrentMonthName()}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="statement-warning-box" style={{
                                backgroundColor: "#fff3cd",
                                padding: "12px",
                                borderRadius: "4px",
                                borderLeft: "4px solid #ffc107",
                                marginBottom: "15px"
                            }}>
                                <strong style={{ color: "#856404" }}>
                                    <FaExclamationCircle style={{ marginRight: "8px" }} />
                                    Warning: This will delete ALL statements for this month!
                                </strong>
                            </div>
                            <p>Are you sure you want to delete <strong>all statements</strong> for <strong>{getCurrentMonthName()}</strong>?</p>
                            <p className="statement-delete-danger-text" style={{ color: "#dc3545", fontWeight: "500" }}>
                                This will delete {totalItems} statement(s). This action cannot be undone!
                            </p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={confirmDelete}
                        disabled={deleting}
                        style={{ display: "flex", alignItems: "center", gap: "8px" }}
                    >
                        {deleting ? (
                            <><Spinner animation="border" size="sm" /> Deleting...</>
                        ) : (
                            <><FaTrashAlt /> Delete</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Email Result Modal */}
            <Modal show={showResultModal} onHide={() => setShowResultModal(false)} centered size="lg" className="statement-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {sendResult?.totalFailed === 0
                            ? <FaCheckCircle style={{ marginRight: "10px", color: "#28a745" }} />
                            : <FaExclamationCircle style={{ marginRight: "10px", color: "#dc3545" }} />}
                        Results
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {sendResult && (
                        <div>
                            {sendResult.message && (
                                <Alert variant={sendResult.totalFailed === 0 ? "success" : "warning"}>
                                    {sendResult.message}
                                </Alert>
                            )}
                            {sendResult.sent && sendResult.sent.length > 0 && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h6 className="statement-result-success-title" style={{ color: '#28a745', marginBottom: '10px' }}>Successfully Processed:</h6>
                                    <div className="statement-result-list statement-result-list-success" style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #d4edda', borderRadius: '4px', padding: '10px' }}>
                                        {sendResult.sent.map((item, index) => (
                                            <div key={index} className={`statement-result-item statement-result-item-success ${index === sendResult.sent.length - 1 ? 'statement-result-item-last' : ''}`} style={{ padding: '5px 0', borderBottom: index < sendResult.sent.length - 1 ? '1px dashed #c3e6cb' : 'none' }}>
                                                <strong>{item.account}</strong>
                                                {item.date && ` - ${formatDateDisplay(item.date)}`}
                                                {item.invoiceNumber && <span> (Invoice: {item.invoiceNumber})</span>}
                                                <br />
                                                {item.email && <small>Email: {item.email}</small>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {sendResult.failed && sendResult.failed.length > 0 && (
                                <div>
                                    <h6 className="statement-result-failed-title" style={{ color: '#dc3545', marginBottom: '10px' }}>
                                        <FaExclamationCircle style={{ marginRight: '5px' }} /> Failed:
                                    </h6>
                                    <div className="statement-result-list statement-result-list-failed" style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #f8d7da', borderRadius: '4px', padding: '10px' }}>
                                        {sendResult.failed.map((item, index) => (
                                            <div key={index} className={`statement-result-item statement-result-item-failed ${index === sendResult.failed.length - 1 ? 'statement-result-item-last' : ''}`} style={{ padding: '5px 0', borderBottom: index < sendResult.failed.length - 1 ? '1px dashed #f5c6cb' : 'none' }}>
                                                <strong>{item.account}</strong> - {item.error || 'Unknown error'}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowResultModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            <div className="statement-footer-wrapper" style={{ width: "95%", margin: "0 auto", padding: "0px", marginTop: "15%" }}>
                <Footer />
            </div>
        </>
    );
};

export default Statement;